import { AddAttachments } from "components/AddAttachments/AddAttachments";
import DatePickerField from "components/controls/DatePickerField";
import { FilteringCombobox } from "components/controls/FilteringCombobox";
import InputField from "components/controls/InputField";
import { RadioGroupField } from "components/controls/RadioGroupField";
import { TextareaField } from "components/controls/TextareaField";
import { ManageImplementationDocuments } from "components/ManageImplementationDocuments/ManageImplementationDocuments";
import { Grid } from "components/styled";
import { UploadFile } from "components/Upload/UploadFile";
import { useFormikContext } from "formik";
import { helpAnchor } from "help/anchor";
import { useCanAddEditPhoto } from "hooks/useCanAddEditPhoto";
import useGetCurrencyOptionsPerUser from "hooks/useGetCurrencyOptionsPerUser";
import { useHandleConsentAutoenrollmentDependency } from "hooks/useHandleConsentAutoenrollmentDependency";
import { checkedOptions } from "library/constants";
import {
  ECheckedValue,
  IBenefitCategory,
  IBenefitImplementation,
  IRowItem,
} from "library/types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { ConsentDependencyFields } from "./ConsentDependencyFields";
import { SelectInsurer } from "./SelectInsurer";
import { SelectPolicy } from "./SelectPolicy";

type Props = {
  benefitImplementation?: IBenefitImplementation;
  currencyCodeOption: any;
  onlyActiveImplementations?: boolean;
  category?: IBenefitCategory;
};

export function ImplementBenefitFormItems({
  currencyCodeOption,
  benefitImplementation,
  onlyActiveImplementations,
  category,
}: Props) {
  const canAddEditPhoto = useCanAddEditPhoto();
  const { t } = useTranslation();
  const { values, setFieldValue, errors } = useFormikContext<FormData>();

  const params = useParams();

  const clientInternalNumberGOS = params?.clientInternalNumberGOS;
  const countryCode = params?.countryCode;

  const { currencyOptionsPerUser } = useGetCurrencyOptionsPerUser();

  const consentsNeeded =
    (values["consentsNeeded"] as ECheckedValue) === ECheckedValue.Yes;

  useHandleConsentAutoenrollmentDependency();

  return (
    <Container>
      <Grid>
        <InputField
          data-help="implementation-dialog-name"
          label={t("greco.name")}
          name="name"
          value={values["name"]}
          setFieldValue={setFieldValue}
          error={errors["name"]}
          required
        />
        <RadioGroupField
          data-help="implementation-dialog-consent-needed"
          value={values["consentsNeeded"]}
          setFieldValue={setFieldValue}
          name="consentsNeeded"
          label={t("hbh.consentNeeded.label")}
          error={errors["consentsNeeded"]}
          options={checkedOptions}
          // required
        />

        <RadioGroupField
          data-help="implementation-dialog-auto-enrollment"
          disabled={consentsNeeded}
          value={values["isAutoEnrollment"]}
          setFieldValue={setFieldValue}
          name="isAutoEnrollment"
          label={t("hbh.isAutoEnrollment.label")}
          options={checkedOptions}
          error={errors["isAutoEnrollment"]}
        />

        <FilteringCombobox
          data-help="implementation-dialog-currency"
          error={errors["currencyCode"]}
          setFieldValue={setFieldValue}
          value={values["currencyCode"]}
          label={t("hbh.currency.label")}
          options={
            currencyCodeOption &&
            !currencyOptionsPerUser?.find(
              (option) => option.code === currencyCodeOption.code
            )
              ? [...currencyOptionsPerUser, currencyCodeOption]
              : currencyOptionsPerUser
          }
          name={"currencyCode"}
          required
        />
        <UploadFile
          disabled={!canAddEditPhoto}
          isUploadAppImages
          setFieldValue={setFieldValue}
          fieldName="coverPhoto"
          defaultValue={values["coverPhoto"]}
          label={canAddEditPhoto ? t("hbh.chooseCoverPicture.label") : t("hbh.coverPicture.label")}
        />
      </Grid>
      {consentsNeeded && <ConsentDependencyFields />}

      <TextareaField
        data-help="implementation-dialog-description"
        label={t("hbh.description.label")}
        name="description"
        value={values["description"]}
        setFieldValue={setFieldValue}
        error={errors["description"]}
        rows={3}
        resize="vertical"
        required
      />
      <Grid $gridColumns={2}>
        <SelectPolicy
          countryCode={
            category
              ? category?.countryCode
              : benefitImplementation
              ? benefitImplementation?.countryCode
              : countryCode
          }
          clientInternalNumberGos={
            category
              ? category?.clientInternalNumberGos
              : benefitImplementation
              ? benefitImplementation?.clientInternalNumberGos
              : clientInternalNumberGOS
          }
        />
        <SelectInsurer
          countryCode={
            category
              ? category?.countryCode
              : benefitImplementation
              ? benefitImplementation?.countryCode
              : countryCode
          }
        />
      </Grid>
      <Grid $gridColumns={3}>
        <InputField
          data-help="implementation-dialog-carrier"
          required
          error={errors["carrierName"]}
          label={t("hbh.carrier.label")}
          name="carrierName"
          value={values["carrierName"]}
          setFieldValue={setFieldValue}
        />
        <InputField
          data-help="implementation-dialog-policy-number"
          required
          error={errors["policyOrContractNumber"]}
          label={t("hbh.policyOrContractNumber.label")}
          name="policyOrContractNumber"
          value={values["policyOrContractNumber"]}
          setFieldValue={setFieldValue}
        />

        <DatePickerField
          dataHelp={helpAnchor("implementation-dialog-valid-from")}
          required
          error={errors["validFrom"]}
          name="validFrom"
          value={values["validFrom"]}
          setFieldValue={setFieldValue}
          label={t("hbh.validFrom.label")}
        />
        <DatePickerField
          dataHelp={helpAnchor("implementation-dialog-valid-to")}
          required
          name="validTo"
          value={values["validTo"]}
          setFieldValue={setFieldValue}
          error={errors["validTo"]}
          label={t("hbh.validTo.label")}
        />
      </Grid>
      {benefitImplementation ? (
        <ManageImplementationDocuments
          dataHelp={helpAnchor("implementation-dialog-documents")}
          item={benefitImplementation as IRowItem}
          onlyActiveImplementations={onlyActiveImplementations}
        />
      ) : (
        <AddAttachments
          dataHelp={helpAnchor("implementation-dialog-attachments")}
        />
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
`;
